import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  SearchIcon, 
  FilterIcon, 
  StethoscopeIcon,
  UserIcon,
  FileTextIcon,
  CalendarIcon,
  ClockIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  BookOpenIcon,
  X
} from 'lucide-react';
import api from '../../lib/api';
import toast from 'react-hot-toast';

interface ClinicalNote {
  id: string;
  patientId: string;
  visitId?: string;
  noteType: 'SOAP' | 'PROGRESS' | 'DISCHARGE' | 'CONSULTATION' | 'PROCEDURE';
  title: string;
  content?: string;
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  notes?: string;
  patient: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  visit?: {
    doctor: {
      user: {
        firstName: string;
        lastName: string;
      };
    };
    visitType: string;
    visitDate: string;
    diagnosis: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Patient {
  id: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Visit {
  id: string;
  visitType: string;
  visitDate: string;
  diagnosis: string;
  doctor: {
    user: {
      firstName: string;
      lastName: string;
    };
  };
}

const ClinicalNotesPage: React.FC = () => {
  const [clinicalNotes, setClinicalNotes] = useState<ClinicalNote[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [noteTypeFilter, setNoteTypeFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ClinicalNote | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    fetchClinicalNotes();
    fetchAnalytics();
    fetchPatients();
  }, []);

  const fetchClinicalNotes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clinical-notes');
      setClinicalNotes(response.data.clinicalNotes || []);
    } catch (error) {
      console.error('Error fetching clinical notes:', error);
      toast.error('Failed to fetch clinical notes');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/clinical-notes/analytics/overview');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.data?.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchVisitsForPatient = async (patientId: string) => {
    try {
      const response = await api.get(`/visits?patientId=${patientId}`);
      setVisits(response.data.visits || []);
    } catch (error) {
      console.error('Error fetching visits for patient:', error);
      setVisits([]);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setShowViewModal(false);
    setShowEditModal(false);
    setSelectedNote(null);
    setSelectedPatientId('');
    setVisits([]);
  };

  const handleViewNote = (note: ClinicalNote) => {
    setSelectedNote(note);
    setShowViewModal(true);
  };

  const handleEditNote = (note: ClinicalNote) => {
    setSelectedNote(note);
    setSelectedPatientId(note.patientId);
    if (note.patientId) {
      fetchVisitsForPatient(note.patientId);
    }
    setShowEditModal(true);
  };

  const handleDeleteNote = async (noteId: string) => {
    if (window.confirm('Are you sure you want to delete this clinical note?')) {
      try {
        await api.delete(`/clinical-notes/${noteId}`);
        toast.success('Clinical note deleted successfully');
        fetchClinicalNotes();
      } catch (error) {
        console.error('Error deleting clinical note:', error);
        toast.error('Failed to delete clinical note');
      }
    }
  };

  const filteredClinicalNotes = clinicalNotes.filter(note => {
    const matchesSearch = 
      (note.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (note.content?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (note.patient?.user?.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (note.patient?.user?.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesType = !noteTypeFilter || note.noteType === noteTypeFilter;
    
    return matchesSearch && matchesType;
  });

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case 'SOAP': return 'bg-blue-100 text-blue-800';
      case 'PROGRESS': return 'bg-green-100 text-green-800';
      case 'DISCHARGE': return 'bg-purple-100 text-purple-800';
      case 'CONSULTATION': return 'bg-yellow-100 text-yellow-800';
      case 'PROCEDURE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getNoteTypeIcon = (type: string) => {
    switch (type) {
      case 'SOAP': return <StethoscopeIcon className="h-4 w-4" />;
      case 'PROGRESS': return <FileTextIcon className="h-4 w-4" />;
      case 'DISCHARGE': return <BookOpenIcon className="h-4 w-4" />;
      case 'CONSULTATION': return <UserIcon className="h-4 w-4" />;
      case 'PROCEDURE': return <StethoscopeIcon className="h-4 w-4" />;
      default: return <FileTextIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clinical Notes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage patient clinical notes and SOAP documentation
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Note
        </button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileTextIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Notes</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <StethoscopeIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">SOAP Notes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.byType?.find((t: any) => t.noteType === 'SOAP')?._count?.noteType || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BookOpenIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.byMonth?.length || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Avg Length</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.averageLength ? `${Math.round(analytics.averageLength)} chars` : '0'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search clinical notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <select
            value={noteTypeFilter}
            onChange={(e) => setNoteTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Types</option>
            <option value="SOAP">SOAP Notes</option>
            <option value="PROGRESS">Progress Notes</option>
            <option value="DISCHARGE">Discharge Notes</option>
            <option value="CONSULTATION">Consultation Notes</option>
            <option value="PROCEDURE">Procedure Notes</option>
          </select>
        </div>
      </div>

      {/* Clinical Notes Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Note Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Doctor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClinicalNotes.map((note) => (
                <tr key={note.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {note.patient.user.firstName} {note.patient.user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {note.patient.user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNoteTypeColor(note.noteType)}`}>
                      {getNoteTypeIcon(note.noteType)}
                      <span className="ml-1">{note.noteType}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{note.title}</div>
                    {note.content && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {note.content.substring(0, 100)}...
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {note.visit?.doctor?.user?.firstName && note.visit?.doctor?.user?.lastName 
                      ? `${note.visit.doctor.user.firstName} ${note.visit.doctor.user.lastName}`
                      : 'No doctor assigned'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(note.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-primary-600 hover:text-primary-900 mr-3" onClick={() => handleViewNote(note)}>
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button className="text-primary-600 hover:text-primary-900 mr-3" onClick={() => handleEditNote(note)}>
                      <EditIcon className="h-4 w-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900" onClick={() => handleDeleteNote(note.id)}>
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredClinicalNotes.length === 0 && (
          <div className="text-center py-12">
            <StethoscopeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No clinical notes found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new clinical note.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Note
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Clinical Note Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Clinical Note</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const noteType = formData.get('noteType') as string;
                
                const noteData = {
                  patientId: formData.get('patientId') as string,
                  visitId: formData.get('visitId') as string || undefined,
                  noteType: noteType as 'SOAP' | 'PROGRESS' | 'DISCHARGE' | 'CONSULTATION' | 'PROCEDURE',
                  title: formData.get('title') as string,
                  content: formData.get('content') as string || undefined,
                  subjective: formData.get('subjective') as string || undefined,
                  objective: formData.get('objective') as string || undefined,
                  assessment: formData.get('assessment') as string || undefined,
                  plan: formData.get('plan') as string || undefined,
                  notes: formData.get('notes') as string || undefined
                };

                try {
                  await api.post('/clinical-notes', noteData);
                  toast.success('Clinical note created successfully');
                  handleCloseModal();
                  fetchClinicalNotes();
                } catch (error) {
                  console.error('Error creating clinical note:', error);
                  toast.error('Failed to create clinical note');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      value={selectedPatientId}
                      onChange={(e) => {
                        setSelectedPatientId(e.target.value);
                        if (e.target.value) {
                          fetchVisitsForPatient(e.target.value);
                        } else {
                          setVisits([]);
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Visit Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Visit (Optional)
                    </label>
                    <select
                      name="visitId"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a visit</option>
                      {visits.length === 0 && selectedPatientId && (
                        <option value="" disabled>No visits found for this patient</option>
                      )}
                      {visits.map((visit) => (
                        <option key={visit.id} value={visit.id}>
                          {visit.visitType} - {new Date(visit.visitDate).toLocaleDateString()} - {visit.diagnosis}
                        </option>
                      ))}
                    </select>
                    {selectedPatientId && (
                      <p className="text-xs text-gray-500 mt-1">
                        {visits.length} visit(s) found for this patient
                      </p>
                    )}
                  </div>

                  {/* Note Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note Type *
                    </label>
                    <select
                      name="noteType"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="SOAP">SOAP Note</option>
                      <option value="PROGRESS">Progress Note</option>
                      <option value="DISCHARGE">Discharge Note</option>
                      <option value="CONSULTATION">Consultation Note</option>
                      <option value="PROCEDURE">Procedure Note</option>
                    </select>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      placeholder="e.g., Initial Assessment"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* SOAP Note Fields - Show only for SOAP notes */}
                <div id="soapFields" className="mt-4 space-y-4">
                  {/* Subjective */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subjective (S)
                    </label>
                    <textarea
                      name="subjective"
                      rows={3}
                      placeholder="Patient's chief complaint, history of present illness, past medical history, medications, allergies, social history..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Objective */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Objective (O)
                    </label>
                    <textarea
                      name="objective"
                      rows={3}
                      placeholder="Physical examination findings, vital signs, laboratory results, imaging studies..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Assessment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assessment (A)
                    </label>
                    <textarea
                      name="assessment"
                      rows={3}
                      placeholder="Differential diagnosis, working diagnosis, problem list..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Plan */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan (P)
                    </label>
                    <textarea
                      name="plan"
                      rows={3}
                      placeholder="Treatment plan, medications, follow-up, patient education, referrals..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* General Content - Show for non-SOAP notes */}
                <div id="generalContent" className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note Content
                  </label>
                  <textarea
                    name="content"
                    rows={6}
                    placeholder="Enter the clinical note content..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Additional Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    placeholder="Any additional notes or comments..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Create Note
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Clinical Note Modal */}
      {showViewModal && selectedNote && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">View Clinical Note</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedNote.patient.user.firstName} {selectedNote.patient.user.lastName}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note Type
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedNote.noteType}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedNote.title}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Visit
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedNote.visit 
                      ? `${selectedNote.visit.visitType} - ${new Date(selectedNote.visit.visitDate).toLocaleDateString()}${selectedNote.visit.diagnosis ? ` - ${selectedNote.visit.diagnosis}` : ''}`
                      : 'No visit associated'
                    }
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Doctor
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedNote.visit?.doctor?.user?.firstName && selectedNote.visit?.doctor?.user?.lastName
                      ? `${selectedNote.visit.doctor.user.firstName} ${selectedNote.visit.doctor.user.lastName}`
                      : 'No doctor assigned'
                    }
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Created
                  </label>
                  <div className="text-sm text-gray-900">
                    {new Date(selectedNote.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note Content
                  </label>
                  <div className="text-sm text-gray-900">
                    {selectedNote.content}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Clinical Note Modal */}
      {showEditModal && selectedNote && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Clinical Note</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const noteType = formData.get('noteType') as string;
                
                const noteData = {
                  patientId: formData.get('patientId') as string,
                  visitId: formData.get('visitId') as string || undefined,
                  noteType: noteType as 'SOAP' | 'PROGRESS' | 'DISCHARGE' | 'CONSULTATION' | 'PROCEDURE',
                  title: formData.get('title') as string,
                  content: formData.get('content') as string || undefined,
                  subjective: formData.get('subjective') as string || undefined,
                  objective: formData.get('objective') as string || undefined,
                  assessment: formData.get('assessment') as string || undefined,
                  plan: formData.get('plan') as string || undefined,
                  notes: formData.get('notes') as string || undefined
                };

                try {
                  await api.put(`/clinical-notes/${selectedNote.id}`, noteData);
                  toast.success('Clinical note updated successfully');
                  handleCloseModal();
                  fetchClinicalNotes();
                } catch (error) {
                  console.error('Error updating clinical note:', error);
                  toast.error('Failed to update clinical note');
                }
              }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Patient Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient *
                    </label>
                    <select
                      name="patientId"
                      required
                      defaultValue={selectedNote.patientId}
                      onChange={(e) => {
                        setSelectedPatientId(e.target.value);
                        if (e.target.value) {
                          fetchVisitsForPatient(e.target.value);
                        } else {
                          setVisits([]);
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a patient</option>
                      {patients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.user.firstName} {patient.user.lastName} - {patient.user.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Visit Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Visit (Optional)
                    </label>
                    <select
                      name="visitId"
                      defaultValue={selectedNote.visitId || ''}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a visit</option>
                      {visits.length === 0 && selectedPatientId && (
                        <option value="" disabled>No visits found for this patient</option>
                      )}
                      {visits.map((visit) => (
                        <option key={visit.id} value={visit.id} selected={visit.id === selectedNote.visitId}>
                          {visit.visitType} - {new Date(visit.visitDate).toLocaleDateString()} - {visit.diagnosis}
                        </option>
                      ))}
                    </select>
                    {selectedPatientId && (
                      <p className="text-xs text-gray-500 mt-1">
                        {visits.length} visit(s) found for this patient
                      </p>
                    )}
                  </div>

                  {/* Note Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note Type *
                    </label>
                    <select
                      name="noteType"
                      required
                      defaultValue={selectedNote.noteType}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="SOAP">SOAP Note</option>
                      <option value="PROGRESS">Progress Note</option>
                      <option value="DISCHARGE">Discharge Note</option>
                      <option value="CONSULTATION">Consultation Note</option>
                      <option value="PROCEDURE">Procedure Note</option>
                    </select>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      defaultValue={selectedNote.title}
                      placeholder="e.g., Initial Assessment"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* SOAP Note Fields - Show only for SOAP notes */}
                <div id="soapFields" className="mt-4 space-y-4">
                  {/* Subjective */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subjective (S)
                    </label>
                    <textarea
                      name="subjective"
                      rows={3}
                      defaultValue={selectedNote.subjective || ''}
                      placeholder="Patient's chief complaint, history of present illness, past medical history, medications, allergies, social history..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Objective */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Objective (O)
                    </label>
                    <textarea
                      name="objective"
                      rows={3}
                      defaultValue={selectedNote.objective || ''}
                      placeholder="Physical examination findings, vital signs, laboratory results, imaging studies..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Assessment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assessment (A)
                    </label>
                    <textarea
                      name="assessment"
                      rows={3}
                      defaultValue={selectedNote.assessment || ''}
                      placeholder="Differential diagnosis, working diagnosis, problem list..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Plan */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan (P)
                    </label>
                    <textarea
                      name="plan"
                      rows={3}
                      defaultValue={selectedNote.plan || ''}
                      placeholder="Treatment plan, medications, follow-up, patient education, referrals..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* General Content - Show for non-SOAP notes */}
                <div id="generalContent" className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note Content
                  </label>
                  <textarea
                    name="content"
                    rows={6}
                    defaultValue={selectedNote.content || ''}
                    placeholder="Enter the clinical note content..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Additional Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    defaultValue={selectedNote.notes || ''}
                    placeholder="Any additional notes or comments..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Update Note
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicalNotesPage; 